import type { DocumentItem, SortDirection, SortState } from './model.js';
import { getValueAtPath } from './path.js';

/**
 * Locale-aware, numeric-aware collator: `file9.pdf` sorts before `file10.pdf`,
 * which plain string comparison gets wrong.
 */
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export const DEFAULT_SORT: SortState = { key: 'name', direction: 'asc', foldersFirst: true };

function toComparable(value: unknown): string | number | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    // Date-ish strings should sort chronologically, not lexically.
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      const t = Date.parse(value);
      if (!Number.isNaN(t)) return t;
    }
    return value;
  }
  return null;
}

/** Read one sort key off an item, coerced to something comparable. */
export function getSortValue(item: DocumentItem, key: string): string | number | null {
  const raw = key.includes('.') ? getValueAtPath(item, key) : (item as unknown as Record<string, unknown>)[key];
  return toComparable(raw);
}

/**
 * Compare two items by one key, ignoring direction.
 *
 * Missing values return 0 here rather than being ranked; `sortDocuments` pins
 * them last *before* applying the direction sign, so "empty rows at the bottom"
 * holds ascending and descending alike.
 */
export function compareBy(a: DocumentItem, b: DocumentItem, key: string): number {
  const av = getSortValue(a, key);
  const bv = getSortValue(b, key);

  if (av === null || bv === null) return 0;
  if (typeof av === 'number' && typeof bv === 'number') return av - bv;
  return collator.compare(String(av), String(bv));
}

/**
 * Sort a copy of `items`. Folders are kept above files by default, which is
 * what every file browser does and what users expect.
 */
export function sortDocuments(items: readonly DocumentItem[], sort: Partial<SortState> = {}): DocumentItem[] {
  const { key, direction, foldersFirst } = { ...DEFAULT_SORT, ...sort };
  const sign = direction === 'desc' ? -1 : 1;

  return [...items].sort((a, b) => {
    if (foldersFirst && a.type !== b.type) return a.type === 'folder' ? -1 : 1;

    // Rank missing values outside the direction sign, so they stay at the
    // bottom whether the user sorted ascending or descending.
    const aMissing = getSortValue(a, key) === null;
    const bMissing = getSortValue(b, key) === null;
    if (aMissing !== bMissing) return aMissing ? 1 : -1;

    const primary = compareBy(a, b, key) * sign;
    if (primary !== 0) return primary;
    // Stable, predictable tiebreak so equal rows never jitter between renders.
    if (key !== 'name') {
      const byName = compareBy(a, b, 'name');
      if (byName !== 0) return byName;
    }
    return collator.compare(a.id, b.id);
  });
}

/** Flip a column: same key toggles direction, a new key starts ascending. */
export function toggleSort(current: SortState, key: string): SortState {
  if (current.key === key) {
    const direction: SortDirection = current.direction === 'asc' ? 'desc' : 'asc';
    return { ...current, direction };
  }
  return { ...current, key, direction: 'asc' };
}
